'use client'

import { useState } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { ArrowRight, Check, Play, ImageIcon, Gem } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StarRating } from '@/components/ui/star-rating'

interface HeroProductProps {
  badge?: string
  badgeVariant?: 'primary' | 'ai' | 'shopify'
  headline: string
  description: string
  featureTags?: string[]
  primaryCTA: {
    text: string
    href: string
  }
  secondaryCTA?: {
    text: string
    href: string
  }
  rating?: {
    value: number
    reviews: number
    href: string
  }
  variant?: 'primary' | 'ai' | 'marketplace'
  media:
    | { type: 'youtube'; videoId: string }
    | { type: 'placeholder' }
    | { type: 'image'; src: string; alt: string }
}

export function HeroProduct({
  badge,
  badgeVariant = 'primary',
  headline,
  description,
  featureTags,
  primaryCTA,
  secondaryCTA,
  rating,
  variant = 'primary',
  media,
}: HeroProductProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const isAi = variant === 'ai'
  const isMarketplace = variant === 'marketplace'

  const bgColor = isAi ? 'bg-stone-950' : 'bg-white'

  return (
    <section
      className={`relative overflow-hidden py-16 md:py-20 lg:py-24 ${bgColor}`}
    >
      <Container className="relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            key={headline}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Badge */}
            {badge && (
              <Badge variant={badgeVariant} className="mb-4">
                {badgeVariant === 'shopify' ? (
                  <Gem className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                {badge}
              </Badge>
            )}

            {/* Headline */}
            <h1
              className={`text-4xl md:text-5xl lg:text-display-2 font-bold mb-4 leading-tight ${
                isAi ? 'text-white' : 'text-stone-900'
              }`}
            >
              {headline}
            </h1>

            {/* Description */}
            <p
              className={`text-lg md:text-xl mb-6 leading-relaxed max-w-xl ${
                isAi ? 'text-stone-300' : 'text-stone-600'
              }`}
            >
              {description}
            </p>

            {/* Feature Tags */}
            {featureTags && featureTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {featureTags.map((tag) => (
                  <span
                    key={tag}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                      isAi
                        ? 'bg-stone-800 text-green-400'
                        : isMarketplace
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-primary-100 text-primary-700'
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
              <Button
                href={primaryCTA.href}
                external={primaryCTA.href.startsWith('http')}
                size="xl"
                variant={isAi ? 'primary' : isMarketplace ? 'marketplace' : 'primary'}
              >
                {primaryCTA.text}
                <ArrowRight className="w-5 h-5" />
              </Button>
              {secondaryCTA && (
                <Button
                  href={secondaryCTA.href}
                  variant={isAi ? 'secondary' : 'secondary'}
                  size="xl"
                  className={isAi ? 'bg-white/10 text-white border-stone-700 hover:bg-white/20' : ''}
                >
                  {secondaryCTA.text}
                </Button>
              )}
            </div>

            {/* Rating */}
            {rating && (
              <div className="flex items-center gap-2">
                <StarRating rating={rating.value} />
                <a
                  href={rating.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm transition-colors ${
                    isAi
                      ? 'text-stone-400 hover:text-stone-200'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  {rating.value} stars ({rating.reviews}+ reviews)
                </a>
              </div>
            )}
          </motion.div>

          {/* Right: Media */}
          <motion.div
            key={headline + '-media'}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center"
          >
            {/* Video/Placeholder container with aspect ratio */}
            {(media.type === 'youtube' || media.type === 'placeholder') && (
              <div
                className={`aspect-video w-full rounded-2xl overflow-hidden shadow-2xl ${
                  isAi ? 'shadow-green-500/30' : isMarketplace ? 'shadow-sky-200/50' : 'shadow-primary-200/50'
                }`}
              >
                {media.type === 'youtube' && (
                  <>
                    {!isPlaying ? (
                      <button
                        onClick={() => setIsPlaying(true)}
                        className="relative w-full h-full bg-stone-900 group"
                        aria-label="Play video"
                      >
                        {/* YouTube Thumbnail */}
                        <Image
                          src={`https://img.youtube.com/vi/${media.videoId}/maxresdefault.jpg`}
                          alt="Video thumbnail"
                          fill
                          className="object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                          sizes="(max-width: 1024px) 100vw, 50vw"
                          unoptimized
                        />
                        {/* Play Button Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div
                            className={`w-20 h-20 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
                              isAi ? 'bg-primary-500' : isMarketplace ? 'bg-sky-500' : 'bg-primary-500'
                            }`}
                          >
                            <Play className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent" />
                      </button>
                    ) : (
                      <iframe
                        src={`https://www.youtube.com/embed/${media.videoId}?autoplay=1&rel=0`}
                        title="Product video"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    )}
                  </>
                )}

                {media.type === 'placeholder' && (
                  <div className="w-full h-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center">
                    <div className="text-stone-400 text-center">
                      <ImageIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                      <span className="text-sm font-medium">Image coming soon</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Image container - no forced aspect ratio, shows full image */}
            {media.type === 'image' && (
              <div className="relative w-full h-[300px] sm:h-[400px] lg:h-[500px]">
                <Image
                  src={media.src}
                  alt={media.alt}
                  fill
                  className="object-contain"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  unoptimized={media.src.startsWith('http') && !media.src.includes('shopify.com')}
                />
              </div>
            )}
          </motion.div>
        </div>
      </Container>
    </section>
  )
}
